// ── SHOPIFY THEME JS ─────────────────────────────────────────────────────────
// By Tide & Twine — Shopify Theme
// Adapted for multi-page Shopify architecture from SPA storefront
// ─────────────────────────────────────────────────────────────────────────────

'use strict';

// ── UTILITIES ────────────────────────────────────────────────────────────────
function formatMoney(cents) {
  return '$' + (cents / 100).toFixed(2);
}

function escapeHtml(str) {
  return String(str).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

function slugify(str) {
  return str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

// ── HEADER SCROLL ────────────────────────────────────────────────────────────
function initHeaderScroll() {
  const header = document.getElementById('site-header');
  if (!header) return;
  window.addEventListener('scroll', () => {
    header.classList.toggle('scrolled', window.scrollY > 40);
  }, { passive: true });
}

// ── SCROLL ANIMATIONS (IntersectionObserver) ─────────────────────────────────
function observeReveals() {
  const revealObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('visible');
        revealObserver.unobserve(entry.target);
      }
    });
  }, { threshold: 0.08, rootMargin: '0px 0px -40px 0px' });

  document.querySelectorAll('.reveal-up, .reveal-right, .reveal-section').forEach(el => {
    revealObserver.observe(el);
  });
}

// Card stagger observer
function observeCardGrids() {
  const cardObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const cards = entry.target.querySelectorAll('.product-card, .review-card');
        cards.forEach((card, i) => {
          setTimeout(() => card.classList.add('card-visible'), i * 80);
        });
        cardObserver.unobserve(entry.target);
      }
    });
  }, { threshold: 0.05 });

  document.querySelectorAll('.products-grid, .featured-grid').forEach(grid => {
    cardObserver.observe(grid);
  });
}

// Instagram grid stagger
function observeInstagramGrid() {
  const igGridObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('ig-visible');
        igGridObserver.unobserve(entry.target);
      }
    });
  }, { threshold: 0.01, rootMargin: '0px 0px 100px 0px' });

  const igGrid = document.getElementById('ig-grid');
  if (igGrid) igGridObserver.observe(igGrid);
}

// ── CART DRAWER ───────────────────────────────────────────────────────────────
function openCart() {
  const drawer = document.getElementById('cart-drawer');
  const overlay = document.getElementById('cart-overlay');
  if (drawer) drawer.classList.add('open');
  if (overlay) overlay.classList.add('open');
  document.body.style.overflow = 'hidden';
}

function closeCart() {
  const drawer = document.getElementById('cart-drawer');
  const overlay = document.getElementById('cart-overlay');
  if (drawer) drawer.classList.remove('open');
  if (overlay) overlay.classList.remove('open');
  document.body.style.overflow = '';
}

function initCartToggle() {
  const toggle = document.getElementById('cart-toggle');
  const closeBtn = document.getElementById('cart-close');
  const overlay = document.getElementById('cart-overlay');
  const continueBtn = document.getElementById('cart-continue-btn');

  if (toggle) {
    toggle.addEventListener('click', () => {
      const drawer = document.getElementById('cart-drawer');
      if (drawer && drawer.classList.contains('open')) {
        closeCart();
      } else {
        openCart();
      }
    });
  }
  if (closeBtn) closeBtn.addEventListener('click', closeCart);
  if (overlay) overlay.addEventListener('click', closeCart);
  if (continueBtn) continueBtn.addEventListener('click', closeCart);
}

async function refreshCart() {
  try {
    const res = await fetch('/cart.js?t=' + Date.now(), { cache: 'no-store', credentials: 'same-origin' });
    if (!res.ok) return;
    const cart = await res.json();
    updateCartDrawer(cart);
  } catch (e) {
    console.warn('Cart refresh failed:', e);
  }
}

async function removeFromCart(lineKey, btn) {
  // Remove by line-item KEY, not variant ID. Variant IDs fail (or hit the wrong line)
  // when the item has line-item properties (e.g. Easify Options) or the same variant
  // is in the cart more than once — Shopify then returns an error and nothing is removed.
  if (btn) { btn.disabled = true; btn.textContent = 'Removing…'; }
  try {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: 0 })
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || err.message || ('HTTP ' + res.status));
    }
    const cart = await res.json();
    updateCartDrawer(cart);
  } catch (e) {
    console.warn('Remove from cart failed:', e.message || e);
    // Never trust local state after a failed change — resync from the server.
    await refreshCart();
  }
}

function updateCartDrawer(cart) {
  // Guard against error payloads (they have no items array) wiping the drawer.
  if (!cart || !Array.isArray(cart.items)) return;

  // Update cart count badge
  ['cart-count', 'cart-count-mobile'].forEach(id => {
    const countEl = document.getElementById(id);
    if (countEl) {
      countEl.textContent = cart.item_count;
      countEl.classList.toggle('visible', cart.item_count > 0);
    }
  });

  const itemsEl = document.getElementById('cart-items');
  const footerEl = document.getElementById('cart-footer');
  const emptyEl = document.getElementById('cart-empty');

  if (!itemsEl) return;

  if (!cart.items || cart.items.length === 0) {
    if (emptyEl) emptyEl.style.display = '';
    if (footerEl) footerEl.style.display = 'none';
    // Clear any item elements (keep empty state)
    const existingItems = itemsEl.querySelectorAll('.cart-item');
    existingItems.forEach(el => el.remove());
    return;
  }

  // Hide empty state
  if (emptyEl) emptyEl.style.display = 'none';

  // Clear previous items
  const existingItems = itemsEl.querySelectorAll('.cart-item');
  existingItems.forEach(el => el.remove());

  // Render items
  cart.items.forEach(item => {
    const itemEl = document.createElement('div');
    itemEl.className = 'cart-item';
    itemEl.innerHTML = `
      <div class="cart-item-img">
        ${item.image ? `<img src="${item.image}" alt="${item.title}">` : ''}
      </div>
      <div class="cart-item-info">
        <p class="cart-item-title">${item.title.split('|')[0].trim()}</p>
        ${item.variant_title && item.variant_title !== 'Default Title' ? `<p class="cart-item-variant">${item.variant_title}</p>` : ''}
        ${Object.entries(item.properties || {}).filter(([k, v]) => v && !k.startsWith('_')).map(([k, v]) => `<p class="cart-item-variant">${escapeHtml(k)}: ${escapeHtml(v)}</p>`).join('')}
        <div class="cart-item-footer">
          <span class="cart-item-price">${formatMoney(item.final_line_price)}</span>
          <button type="button" class="cart-item-remove" data-line-key="${item.key}">Remove</button>
        </div>
      </div>
    `;
    const removeBtn = itemEl.querySelector('.cart-item-remove');
    removeBtn.addEventListener('click', () => {
      removeFromCart(item.key, removeBtn);
    });
    // Insert before footer
    itemsEl.appendChild(itemEl);
  });

  // Update subtotal
  const subtotalEl = document.getElementById('cart-subtotal-amt');
  if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);

  // Free-shipping progress bar
  updateShipProgress(cart.total_price);
  renderCartUpsell(cart);
  syncGiftWrap(cart);

  if (footerEl) footerEl.style.display = '';
}

// ── BAG UPSELL: "Complete the set" while under the free-shipping line ────────
const _recCache = {};
let _strapCache = null;
const OPTION_WORDS = /personaliz|create your own|varsity|custom|monogram/i;

async function getRecommendations(productId) {
  if (_recCache[productId]) return _recCache[productId];
  try {
    const r = await fetch(`/recommendations/products.json?product_id=${productId}&limit=10`);
    const d = r.ok ? await r.json() : { products: [] };
    _recCache[productId] = d.products || [];
  } catch (e) { _recCache[productId] = []; }
  return _recCache[productId];
}
// Smaller, different-type pieces that pair well: makeup bags and wristlets.
let _poolCache = null;
async function getAddOnPool() {
  if (_poolCache) return _poolCache;
  const out = [];
  for (const handle of ['makeup-bags', 'wristlets']) {
    try {
      const r = await fetch(`/collections/${handle}/products.json?limit=30`);
      if (!r.ok) continue;
      const d = await r.json();
      (d.products || []).forEach(p => {
        const vars = (p.variants || []).map(v => ({ id: v.id, title: v.title, available: v.available, price: Math.round(parseFloat(v.price) * 100) }));
        const avail = vars.filter(v => v.available);
        if (!avail.length) return;
        out.push({
          id: p.id, title: p.title, type: p.product_type, url: `/products/${p.handle}`, available: true,
          price: Math.min(...avail.map(v => v.price)),
          featured_image: p.images && p.images[0] ? p.images[0].src : '',
          variants: vars
        });
      });
    } catch (e) {}
  }
  _poolCache = out;
  return out;
}

async function getStrap() {
  if (_strapCache !== null) return _strapCache;
  try {
    const r = await fetch('/products/carrying-strap-for-bikini-bags.js');
    _strapCache = r.ok ? await r.json() : false;
  } catch (e) { _strapCache = false; }
  return _strapCache;
}

async function renderCartUpsell(cart) {
  const wrap = document.getElementById('cart-upsell');
  const list = document.getElementById('cart-upsell-list');
  const drawer = document.getElementById('cart-drawer');
  if (!wrap || !list || !drawer) return;
  const threshold = parseInt(drawer.dataset.freeShippingThreshold) || 10000;
  const remaining = threshold - cart.total_price;
  if (!cart.items.length || remaining <= 0) { wrap.hidden = true; return; }

  const inCart = new Set(cart.items.map(i => i.product_id));
  const picks = [];

  const hasBikiniBag = cart.items.some(i => /bikini bag/i.test(i.product_type || ''));
  if (hasBikiniBag) {
    const strap = await getStrap();
    if (strap && strap.available && !inCart.has(strap.id)) picks.push(strap);
  }

  const typesInCart = new Set(cart.items.map(i => (i.product_type || '').toLowerCase()));
  const recs = [
    ...(await getRecommendations(cart.items[cart.items.length - 1].product_id)),
    ...(await getAddOnPool())
  ];
  recs
    .filter(p => p.available && !inCart.has(p.id) && !OPTION_WORDS.test(p.title) && !/gift-wrap/.test(p.url || p.handle || '') && p.title !== 'Gift Wrap' && !picks.some(x => x.id === p.id))
    .filter(p => !typesInCart.has(String(p.type || p.product_type || '').toLowerCase()))
    .filter((p, i, arr) => arr.findIndex(x => x.id === p.id) === i)
    .sort((a, b) => {
      const score = p => (p.price >= remaining ? p.price - remaining : (remaining - p.price) * 2);
      return score(a) - score(b);
    })
    .slice(0, 3 - picks.length)
    .forEach(p => picks.push(p));

  if (!picks.length) { wrap.hidden = true; return; }

  const title = document.getElementById('cart-upsell-title');
  if (title) title.textContent = `Complete the set · ${formatMoney(remaining)} to free shipping`;

  list.innerHTML = '';
  picks.forEach(p => {
    const avail = (p.variants || []).filter(v => v.available);
    const img = p.featured_image ? (p.featured_image.startsWith('//') ? 'https:' + p.featured_image : p.featured_image) : '';
    const row = document.createElement('div');
    row.className = 'cart-upsell-item';
    const multi = avail.length > 1;
    const smallChoice = multi && avail.length <= 4;
    row.innerHTML = `
      ${img ? `<img src="${img}${img.includes('?') ? '&' : '?'}width=120" alt="" loading="lazy">` : '<img alt="">'}
      <div class="cart-upsell-info">
        <a href="${p.url}">${escapeHtml(p.title.split('|')[0].trim())}</a>
        <span>${formatMoney(p.price)}</span>
      </div>
      ${smallChoice ? `<select class="cart-upsell-select" aria-label="Option">${avail.map(v => `<option value="${v.id}">${escapeHtml(v.title)}</option>`).join('')}</select>` : ''}
      ${multi && !smallChoice ? `<a class="cart-upsell-add" href="${p.url}">Choose</a>` : `<button type="button" class="cart-upsell-add">Add</button>`}
    `;
    const addBtn = row.querySelector('button.cart-upsell-add');
    if (addBtn) {
      addBtn.addEventListener('click', async () => {
        const sel = row.querySelector('select');
        const id = sel ? parseInt(sel.value) : (avail[0] && avail[0].id);
        if (!id) return;
        addBtn.disabled = true; addBtn.textContent = '…';
        try {
          const r = await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: [{ id, quantity: 1 }] }) });
          if (!r.ok) throw new Error('add failed');
        } catch (e) { addBtn.disabled = false; addBtn.textContent = 'Add'; return; }
        refreshCart();
      });
    }
    list.appendChild(row);
  });
  wrap.hidden = false;
}

// ── GIFT WRAP (generic $ add-on product, handle "gift-wrap") ─────────────
function isGiftWrapItem(item) { return item && (item.handle === 'gift-wrap' || /(^|\/)gift-wrap(\?|$)/.test(item.url || '')); }
function syncGiftWrap(cart) {
  const box = document.getElementById('cart-giftwrap-check');
  if (!box) return;
  const wrapLine = cart.items.find(isGiftWrapItem);
  box.checked = !!wrapLine;
  // Gift wrap alone in the bag makes no sense: hide the option when nothing else is there.
  const others = cart.items.filter(i => !isGiftWrapItem(i));
  box.closest('.cart-giftwrap').style.display = others.length ? '' : 'none';
  if (!others.length && wrapLine) {
    fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: wrapLine.key, quantity: 0 }) }).then(refreshCart);
  } else if (wrapLine && wrapLine.quantity > 1) {
    fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: wrapLine.key, quantity: 1 }) }).then(refreshCart);
  }
}
function initGiftWrap() {
  const box = document.getElementById('cart-giftwrap-check');
  if (!box) return;
  box.addEventListener('change', async () => {
    box.disabled = true;
    try {
      if (box.checked) {
        await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: [{ id: parseInt(box.dataset.variantId), quantity: 1 }] }) });
      } else {
        const cart = await fetch('/cart.js?t=' + Date.now(), { cache: 'no-store' }).then(r => r.json());
        const line = cart.items.find(isGiftWrapItem);
        if (line) await fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: line.key, quantity: 0 }) });
      }
    } catch (e) { console.warn('Gift wrap toggle failed', e); }
    box.disabled = false;
    refreshCart();
  });
}

// ── GIFT NOTE (cart note) ───────────────────────────────────────────────────
function initCartNote() {
  const ta = document.getElementById('cart-note');
  const status = document.getElementById('cart-note-status');
  if (!ta) return;
  let t;
  ta.addEventListener('input', () => {
    clearTimeout(t);
    if (status) status.textContent = '';
    t = setTimeout(async () => {
      try {
        const r = await fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: ta.value }) });
        if (status) status.textContent = r.ok ? 'Saved' : 'Could not save note';
      } catch (e) { if (status) status.textContent = 'Could not save note'; }
    }, 600);
  });
}

function updateShipProgress(totalPrice) {
  const drawer = document.getElementById('cart-drawer');
  const msgEl = document.getElementById('cart-ship-msg');
  const fillEl = document.getElementById('cart-ship-fill');
  if (!drawer || !msgEl || !fillEl) return;

  const threshold = parseInt(drawer.dataset.freeShippingThreshold) || 10000;
  const remaining = threshold - totalPrice;

  if (remaining > 0) {
    const pct = Math.min(100, Math.round((totalPrice / threshold) * 100));
    msgEl.classList.remove('is-unlocked');
    msgEl.innerHTML = `You're <strong>${formatMoney(remaining)}</strong> away from free shipping`;
    fillEl.style.width = pct + '%';
  } else {
    msgEl.classList.add('is-unlocked');
    msgEl.innerHTML = `✓ You've unlocked free shipping!`;
    fillEl.style.width = '100%';
  }
}

// Collect line-item properties written by product-option apps (Easify writes
// hidden inputs named "properties[...]" next to the buy buttons). Without this,
// personalization (e.g. "Name to Embroider") never reaches the order.
function collectLineItemProperties() {
  const scope = document.querySelector('.product-info') || document;
  const props = {};
  scope.querySelectorAll('[name^="properties["]').forEach(el => {
    if (el.disabled) return;
    if ((el.type === 'checkbox' || el.type === 'radio') && !el.checked) return;
    const key = el.name.slice('properties['.length, -1);
    const val = (el.value || '').trim();
    if (!key || !val) return;
    if (props[key] && props[key].split(', ').includes(val)) return;
    props[key] = props[key] ? props[key] + ', ' + val : val;
  });
  return props;
}

// ── ADD TO CART (product page) ────────────────────────────────────────────────
function showProductError(msg) {
  const el = document.getElementById('product-form-error');
  if (!el) { if (msg) alert(msg); return; }
  el.textContent = msg;
  el.hidden = !msg;
}
function clearProductError() { showProductError(''); }

// Personalization guard: if the chosen variant is a paid personalization option,
// make sure the customer actually typed something into the option-app field.
function personalizationMissing(variantTitle) {
  const scope = document.querySelector('.product-info') || document;
  // Required fields flagged by the options app
  const required = [...scope.querySelectorAll('.easify input[required], .easify textarea[required], .easify [aria-required="true"]')]
    .filter(el => el.offsetParent !== null && !String(el.value || '').trim());
  if (required.length) return required[0];
  const t = String(variantTitle || '').toLowerCase();
  const wantsName = /(add|with)[^/]*\b(name|embroider|monogram|letter)/.test(t);
  if (!wantsName) return null;
  const props = collectLineItemProperties();
  if (Object.keys(props).length) return null;
  const visibleText = [...scope.querySelectorAll('.easify input[type="text"], .easify textarea')].find(el => el.offsetParent !== null);
  return visibleText || scope.querySelector('.easify') || document.getElementById('product-variants');
}

async function addToCartShopify(sourceBtn) {
  const mainBtn = document.getElementById('add-to-cart-btn');
  if (!mainBtn) return;
  const btn = sourceBtn instanceof HTMLElement ? sourceBtn : mainBtn;

  const variantId = parseInt(mainBtn.dataset.variantId);
  if (!variantId) {
    showProductError('Please choose your options first.');
    return;
  }

  let variantTitle = '';
  try {
    const pd = JSON.parse(document.getElementById('product-data').textContent);
    const v = pd.variants.find(x => x.id === variantId);
    variantTitle = v ? v.title : '';
  } catch (e) {}

  const missing = personalizationMissing(variantTitle);
  if (missing) {
    showProductError('Please enter the name or letters you want added before adding to your bag.');
    missing.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (missing.focus) setTimeout(() => missing.focus(), 300);
    return;
  }
  clearProductError();

  const qtyEl = document.getElementById('qty-num');
  const qty = parseInt(qtyEl ? qtyEl.textContent : '1') || 1;

  const items = [{ id: variantId, quantity: qty, properties: collectLineItemProperties() }];
  const strapCheck = document.getElementById('strap-addon-check');
  const strapVar = document.getElementById('strap-addon-variant');
  if (strapCheck && strapCheck.checked && strapVar && parseInt(strapVar.value)) {
    items.push({ id: parseInt(strapVar.value), quantity: qty });
  }

  btn.disabled = true;
  const btnSpan = btn.querySelector('span');
  if (btnSpan) btnSpan.textContent = 'Adding...';

  try {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items })
    });

    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      throw new Error(errData.description || errData.message || 'Could not add to your bag. Please try again.');
    }

    await refreshCart();
    openCart();
    if (strapCheck) strapCheck.checked = false;

    if (btnSpan) btnSpan.textContent = 'Added! ✓';
    btn.style.background = 'var(--c-tide)';
    setTimeout(() => {
      if (btnSpan) btnSpan.textContent = 'Add to Bag';
      btn.style.background = '';
      btn.disabled = false;
    }, 2000);

  } catch (e) {
    console.warn('Add to cart error:', e.message);
    if (btnSpan) btnSpan.textContent = 'Add to Bag';
    btn.disabled = false;
    showProductError(e.message || 'Could not add to your bag. Please try again.');
  }
}

// Ship-by promise: 7 business days from today, shown as a concrete date.
function initShipByDate() {
  const el = document.getElementById('ship-by-text');
  if (!el) return;
  // 7 business days (Mon–Fri) from the shopper's current date (never hardcoded).
  const d = new Date();
  let added = 0;
  while (added < 7) {
    d.setDate(d.getDate() + 1);
    const day = d.getDay();
    if (day !== 0 && day !== 6) added++;
  }
  const label = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  el.textContent = `Handmade to order · ships by ${label}`;
}

function initAddToCart() {
  const form = document.getElementById('product-form');
  if (form) {
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      addToCartShopify(document.getElementById('add-to-cart-btn'));
    });
  }
  const btn = document.getElementById('add-to-cart-btn');
  if (btn) {
    btn.addEventListener('click', () => addToCartShopify(btn));
  }
  const stickyBtn = document.getElementById('sticky-add-to-cart-btn');
  if (stickyBtn) {
    stickyBtn.addEventListener('click', () => addToCartShopify(stickyBtn));
  }
}

// Quick-add to cart from product cards (single-variant products)
function initCardQuickAdd() {
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('.card-quick-add');
    if (!btn) return;
    // The card is wrapped in an <a>; stop the click from navigating to the product.
    e.preventDefault();
    e.stopPropagation();

    const variantId = parseInt(btn.dataset.variantId);
    if (!variantId || btn.disabled) return;

    btn.disabled = true;
    const original = btn.textContent;
    btn.textContent = 'Adding…';

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      });
      if (!res.ok) throw new Error('Add to cart failed');
      await refreshCart();
      openCart();
      btn.textContent = 'Added ✓';
    } catch (err) {
      console.warn('Quick add failed:', err.message);
      btn.textContent = original;
    } finally {
      setTimeout(() => { btn.textContent = original; btn.disabled = false; }, 1500);
    }
  });
}

// Show the sticky mobile add-to-cart bar once the main button scrolls out of view
function initStickyATC() {
  const sticky = document.getElementById('sticky-atc');
  const mainBtn = document.getElementById('add-to-cart-btn');
  if (!sticky || !mainBtn) return;

  const obs = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      sticky.classList.toggle('visible', !entry.isIntersecting);
    });
  }, { threshold: 0 });
  obs.observe(mainBtn);
}

// ── QTY SELECTOR ─────────────────────────────────────────────────────────────
let currentQty = 1;

function changeQty(delta) {
  currentQty = Math.max(1, currentQty + delta);
  const el = document.getElementById('qty-num');
  if (el) el.textContent = currentQty;
  const qtyInput = document.getElementById('product-form-qty');
  if (qtyInput) qtyInput.value = currentQty;
}

function initQtySelector() {
  const minusBtn = document.getElementById('qty-minus');
  const plusBtn = document.getElementById('qty-plus');
  if (minusBtn) minusBtn.addEventListener('click', () => changeQty(-1));
  if (plusBtn) plusBtn.addEventListener('click', () => changeQty(1));
}

// ── PRODUCT PAGE ─────────────────────────────────────────────────────────────
function initProductPage() {
  const dataEl = document.getElementById('product-data');
  const imagesEl = document.getElementById('product-images');
  if (!dataEl) return; // Not a product page

  let product, imageUrls;
  try {
    product = JSON.parse(dataEl.textContent);
    imageUrls = imagesEl ? JSON.parse(imagesEl.textContent) : [];
  } catch (e) {
    console.warn('Failed to parse product data:', e);
    return;
  }

  initGallery(imageUrls);
  initVariantPicker(product);
}

function initGallery(imageUrls) {
  if (!imageUrls || !imageUrls.length) return;

  let currentIndex = 0;
  const mainImg = document.getElementById('gallery-main-img');
  if (!mainImg) return;

  mainImg.style.transition = 'opacity 0.15s ease';

  function switchGalleryImage(index) {
    const clamped = Math.max(0, Math.min(index, imageUrls.length - 1));
    if (clamped === currentIndex) return;
    currentIndex = clamped;

    mainImg.style.opacity = '0';
    setTimeout(() => {
      mainImg.src = imageUrls[clamped];
      mainImg.style.opacity = '1';
    }, 150);

    const thumbs = document.querySelectorAll('.gallery-thumb');
    thumbs.forEach((t, i) => {
      t.classList.toggle('active', i === clamped);
      if (i === clamped) {
        t.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      }
    });
  }

  // Wire up thumbnails (rendered by Liquid, but we add click handlers)
  const thumbsContainer = document.getElementById('gallery-thumbs');
  if (thumbsContainer) {
    thumbsContainer.addEventListener('click', (e) => {
      const thumb = e.target.closest('.gallery-thumb');
      if (!thumb) return;
      const index = parseInt(thumb.dataset.index || '0');
      switchGalleryImage(index);
    });
  }
}

function initVariantPicker(product) {
  const container = document.getElementById('product-variants');
  if (!container) return;
  if (!product.options || product.options.length === 0) return;

  // If product only has "Default Title" as single option, skip
  if (product.options.length === 1 && product.options[0] === 'Title') return;

  const selectedOptions = {};
  product.options.forEach((optName, i) => {
    const firstVariant = product.variants[0];
    selectedOptions[optName] = firstVariant ? firstVariant[`option${i + 1}`] : null;
  });

  product.options.forEach((optName, optIdx) => {
    const values = [...new Set(
      product.variants
        .map(v => v[`option${optIdx + 1}`])
        .filter(Boolean)
    )];
    if (!values.length) return;

    const group = document.createElement('div');
    group.className = 'variant-group';

    const optSlug = slugify(optName);
    group.innerHTML = `
      <p class="variant-label">
        ${optName}:
        <span class="variant-selected" id="vsel-${optSlug}">${selectedOptions[optName] || ''}</span>
      </p>
      <div class="variant-options" id="vopts-${optSlug}"></div>
    `;

    const optsContainer = group.querySelector('.variant-options');

    values.forEach(val => {
      const btn = document.createElement('button');
      btn.className = 'variant-btn' + (val === selectedOptions[optName] ? ' active' : '');
      btn.textContent = val;
      btn.type = 'button';

      btn.addEventListener('click', () => {
        selectedOptions[optName] = val;

        const selDisplay = document.getElementById(`vsel-${optSlug}`);
        if (selDisplay) selDisplay.textContent = val;

        optsContainer.querySelectorAll('.variant-btn').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');

        updateSelectedVariant();
      });

      optsContainer.appendChild(btn);
    });

    container.appendChild(group);
  });

  // Show dimensions from the description right under the size buttons.
  const descText = ((document.querySelector('.product-description') || {}).innerText || '').replace(/\s+/g, ' ');
  function findSizeNote(value) {
    if (!value || !descText) return '';
    const key = value.split(/\s+/)[0].replace(/[-\s]/g, '').toLowerCase();
    if (key.length < 3) return '';
    const re = /([A-Za-z][A-Za-z\- ]{1,20}?)\s*:\s*([^:]{4,140}?)(?=\s+[A-Z][A-Za-z\-]+\s*:|\s*\+\+|\s+Care\b|$)/g;
    let m;
    while ((m = re.exec(descText))) {
      const label = m[1].replace(/[-\s]/g, '').toLowerCase();
      if (label.endsWith(key) || key.startsWith(label)) {
        const dims = m[2].trim();
        if (/\d/.test(dims) && /["”]|inch|in\b/i.test(dims)) return dims;
      }
    }
    return '';
  }
  function updateSizeNotes() {
    product.options.forEach((optName) => {
      const slug = slugify(optName);
      const opts = document.getElementById(`vopts-${slug}`);
      if (!opts) return;
      let note = document.getElementById(`vnote-${slug}`);
      const text = findSizeNote(selectedOptions[optName]);
      if (!text) { if (note) note.hidden = true; return; }
      if (!note) {
        note = document.createElement('p');
        note.className = 'variant-size-note';
        note.id = `vnote-${slug}`;
        opts.insertAdjacentElement('afterend', note);
      }
      note.hidden = false;
      note.textContent = '📏 ' + text;
    });
  }

  // Start from ?variant= if present
  try {
    const vParam = new URL(window.location.href).searchParams.get('variant');
    const start = vParam && product.variants.find(v => String(v.id) === vParam);
    if (start) {
      product.options.forEach((opt, i) => {
        selectedOptions[opt] = start[`option${i + 1}`];
        const slug = slugify(opt);
        const sel = document.getElementById(`vsel-${slug}`);
        if (sel) sel.textContent = selectedOptions[opt];
        const opts = document.getElementById(`vopts-${slug}`);
        if (opts) opts.querySelectorAll('.variant-btn').forEach(b => b.classList.toggle('active', b.textContent === selectedOptions[opt]));
      });
    }
  } catch (e) {}
  updateSelectedVariant();

  function updateSelectedVariant() {
    const variant = product.variants.find(v =>
      product.options.every((opt, i) => v[`option${i + 1}`] === selectedOptions[opt])
    );

    const addBtnEl = document.getElementById('add-to-cart-btn');
    const stickyBtnEl = document.getElementById('sticky-add-to-cart-btn');
    const setBuyState = (label, disabled) => {
      [addBtnEl, stickyBtnEl].forEach(b => {
        if (!b) return;
        b.disabled = disabled;
        const span = b.querySelector('span');
        if (span) span.textContent = label;
      });
    };
    updateSizeNotes();
    clearProductError();

    if (!variant) {
      setBuyState('Unavailable', true);
      return;
    }
    setBuyState(variant.available ? 'Add to Bag' : 'Sold Out', !variant.available);

    if (variant) {
      const addBtn = document.getElementById('add-to-cart-btn');
      if (addBtn) addBtn.dataset.variantId = variant.id;
      const idInput = document.getElementById('product-form-variant-id');
      if (idInput) idInput.value = variant.id;
      try {
        const u = new URL(window.location.href);
        u.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', u.toString());
      } catch (e) {}

      const priceEl = document.getElementById('product-price');
      if (priceEl) priceEl.textContent = formatMoney(variant.price);

      const stickyPriceEl = document.getElementById('sticky-atc-price');
      if (stickyPriceEl) stickyPriceEl.textContent = formatMoney(variant.price);

      const compareEl = document.getElementById('product-compare-price');
      if (compareEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          compareEl.textContent = formatMoney(variant.compare_at_price);
          compareEl.style.display = '';
        } else {
          compareEl.style.display = 'none';
        }
      }
    }
  }
}

// ── ACCORDION ────────────────────────────────────────────────────────────────
function initAccordions() {
  document.querySelectorAll('.accordion-trigger[data-accordion]').forEach(trigger => {
    trigger.addEventListener('click', () => {
      const body = trigger.nextElementSibling;
      const isOpen = body.classList.contains('open');

      // Close all
      document.querySelectorAll('.accordion-trigger').forEach(t => {
        t.classList.remove('open');
        if (t.nextElementSibling) t.nextElementSibling.classList.remove('open');
      });

      // Open clicked (if was closed)
      if (!isOpen) {
        trigger.classList.add('open');
        body.classList.add('open');
      }
    });
  });
}

// ── REVIEWS CAROUSEL ─────────────────────────────────────────────────────────
// Reviews are pre-rendered by Liquid section blocks.
// JS handles the carousel sliding behavior.
let reviewIndex = 0;
let reviewVisible = 3;

function getReviewVisible() {
  if (window.innerWidth <= 600) return 1;
  if (window.innerWidth <= 900) return 2;
  return 3;
}

function initReviewsCarousel() {
  const carousel = document.getElementById('reviews-carousel');
  const dotsContainer = document.getElementById('review-dots');
  const prevBtn = document.getElementById('review-prev');
  const nextBtn = document.getElementById('review-next');
  if (!carousel) return;

  const cards = carousel.querySelectorAll('.review-card');
  if (!cards.length) return;

  reviewVisible = getReviewVisible();
  reviewIndex = 0;

  // Build dots
  function buildDots() {
    if (!dotsContainer) return;
    const totalPages = Math.ceil(cards.length / reviewVisible);
    dotsContainer.innerHTML = '';
    for (let i = 0; i < totalPages; i++) {
      const dot = document.createElement('button');
      dot.className = 'carousel-dot' + (i === 0 ? ' active' : '');
      dot.setAttribute('aria-label', `Go to page ${i + 1}`);
      dot.setAttribute('type', 'button');
      dot.addEventListener('click', () => {
        reviewIndex = i * reviewVisible;
        updateReviewCarousel(cards, prevBtn, nextBtn);
      });
      dotsContainer.appendChild(dot);
    }
  }

  buildDots();

  if (prevBtn) {
    prevBtn.addEventListener('click', () => {
      reviewIndex = Math.max(0, reviewIndex - 1);
      updateReviewCarousel(cards, prevBtn, nextBtn);
    });
  }
  if (nextBtn) {
    nextBtn.addEventListener('click', () => {
      reviewIndex = Math.min(cards.length - reviewVisible, reviewIndex + 1);
      updateReviewCarousel(cards, prevBtn, nextBtn);
    });
  }

  updateReviewCarousel(cards, prevBtn, nextBtn);

  // Responsive reflow
  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      const newVisible = getReviewVisible();
      if (newVisible !== reviewVisible) {
        reviewVisible = newVisible;
        reviewIndex = 0;
        buildDots();
        updateReviewCarousel(cards, prevBtn, nextBtn);
      }
    }, 100);
  });
}

function updateReviewCarousel(cards, prevBtn, nextBtn) {
  const carousel = document.getElementById('reviews-carousel');
  if (!carousel || !cards.length) return;

  const gap = 24; // 1.5rem
  const cardW = cards[0].offsetWidth;
  const offset = reviewIndex * (cardW + gap);
  carousel.style.transform = `translateX(-${offset}px)`;

  // Update dots
  const currentPage = Math.floor(reviewIndex / reviewVisible);
  document.querySelectorAll('.carousel-dot').forEach((dot, i) => {
    dot.classList.toggle('active', i === currentPage);
  });

  // Disable arrows at boundaries
  if (prevBtn) prevBtn.disabled = reviewIndex === 0;
  if (nextBtn) nextBtn.disabled = reviewIndex >= cards.length - reviewVisible;
}

// ── NEWSLETTER FORM ───────────────────────────────────────────────────────────
function initNewsletterForm() {
  const form = document.getElementById('newsletter-form');
  if (!form) return;

  // Shopify's native contact form handles submission.
  // This just adds a visual loading state on submit.
  form.addEventListener('submit', () => {
    const btn = form.querySelector('button[type="submit"]');
    if (btn) {
      btn.textContent = 'Subscribing...';
      btn.disabled = true;
    }
  });
}

// ── COLLECTION PAGE SORT ──────────────────────────────────────────────────────
// Sort select uses URL navigation — handled inline in collection-template.liquid
// No additional JS needed.

// ── MOBILE NAV ────────────────────────────────────────────────────────────────
(function () {
  const menuBtn = document.getElementById('menu-toggle');
  const mobileNav = document.getElementById('mobile-nav');
  if (!menuBtn || !mobileNav) return;

  function openMenu() {
    mobileNav.classList.add('is-open');
    menuBtn.classList.add('is-open');
    menuBtn.setAttribute('aria-expanded', 'true');
    mobileNav.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }

  function closeMenu() {
    mobileNav.classList.remove('is-open');
    menuBtn.classList.remove('is-open');
    menuBtn.setAttribute('aria-expanded', 'false');
    mobileNav.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  menuBtn.addEventListener('click', function () {
    if (mobileNav.classList.contains('is-open')) closeMenu();
    else openMenu();
  });

  // Close button inside the overlay
  var closeBtn = document.getElementById('mobile-nav-close');
  if (closeBtn) closeBtn.addEventListener('click', closeMenu);

  // Close when a nav link is tapped
  mobileNav.querySelectorAll('.mobile-nav-link').forEach(function (link) {
    link.addEventListener('click', closeMenu);
  });

  // Wire mobile cart button to the same cart drawer
  const mobileCartBtn = document.getElementById('cart-toggle-mobile');
  const desktopCartBtn = document.getElementById('cart-toggle');
  if (mobileCartBtn && desktopCartBtn) {
    mobileCartBtn.addEventListener('click', function () {
      desktopCartBtn.click();
    });
  }
})();

// ── MAIN INIT ─────────────────────────────────────────────────────────────────
// Expose the real fixed-header height so page offsets don't rely on a hard-coded 148px.
function setHeaderHeightVar() {
  const header = document.getElementById('site-header');
  if (!header) return;
  const bottom = Math.round(header.getBoundingClientRect().bottom + (window.scrollY > 0 ? 0 : 0));
  if (window.scrollY < 5 && bottom > 0) document.documentElement.style.setProperty('--header-h', bottom + 'px');
}

function init() {
  setHeaderHeightVar();
  window.addEventListener('resize', setHeaderHeightVar);
  initHeaderScroll();
  observeReveals();
  observeCardGrids();
  observeInstagramGrid();
  initCartToggle();
  initAddToCart();
  initCardQuickAdd();
  initStickyATC();
  initQtySelector();
  initAccordions();
  initProductPage();
  initShipByDate();
  initCartNote();
  initGiftWrap();
  initReviewsCarousel();
  initNewsletterForm();
  refreshCart(); // sync cart count on every page load
}

// Back/forward cache restores a stale DOM snapshot — resync the cart when that happens.
window.addEventListener('pageshow', (e) => { if (e.persisted) refreshCart(); });

// DOM ready — scripts are deferred so DOM is already parsed
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
